import { useEffect, useState } from 'react';
import { useAppSelector } from '../../../../hooks/redux';
import { stationHasActiveTransfer } from '../../EquipmentLod3D';
import { transferAnimationPipelineStore } from '../../../../store/transferAnimationPipelineStore';
import { stockerRackDisplayStore } from './stockerRackDisplayStore';

function clampCount(count: number, totalSlots: number): number {
  return Math.min(Math.max(0, totalSlots), Math.max(0, Math.floor(count)));
}

/**
 * Effective rack FOUP count for Stocker3D rendering.
 * During live transfer the anim store may lead authoritative inventory until
 * the crane motion completes and the stream commits the new occupancy.
 */
export function useStockerRackDisplayCount(
  elementId: string | undefined,
  authoritativeCount: number | undefined,
  totalSlots: number,
  demoFillRatio = 0.82,
  stationAliases: readonly string[] = []
): number {
  const running = useAppSelector((state) => state.simulation.running);
  const [, tick] = useState(0);
  const pipelineHeld = (() => {
    const ids = new Set<string>();
    if (elementId) ids.add(elementId);
    for (const alias of stationAliases) {
      if (alias) ids.add(alias);
    }
    for (const id of ids) {
      if (transferAnimationPipelineStore.shouldHoldInventory(id)) return true;
    }
    return false;
  })();

  useEffect(() => {
    if (!elementId) return;
    const unsubRack = stockerRackDisplayStore.subscribe(() => tick((n) => n + 1));
    const unsubPipeline = transferAnimationPipelineStore.subscribe(() => tick((n) => n + 1));
    return () => {
      unsubRack();
      unsubPipeline();
    };
  }, [elementId]);

  if (elementId && running && stockerRackDisplayStore.has(elementId)) {
    const display = stockerRackDisplayStore.get(elementId)!;
    // While the OHT ↔ stocker transfer runs, only the crane animation may change
    // rack occupancy — raw inventory can arrive on the sim clock too early.
    if (stationHasActiveTransfer(elementId) || pipelineHeld) {
      return clampCount(display, totalSlots);
    }
    const auth = authoritativeCount ?? 0;
    return clampCount(Math.max(display, auth), totalSlots);
  }

  if (authoritativeCount != null) {
    return clampCount(authoritativeCount, totalSlots);
  }

  return clampCount(Math.round(totalSlots * demoFillRatio), totalSlots);
}
